import { CampaignsInsights, Client } from '@app/types/api';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Table,
    Button,
    Flex,
    Space,
    Skeleton,
    Empty,
    type ColumnsType,
    type SortOrder,
} from '@app/ui';
import { DownloadOutlined } from '@ant-design/icons';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import getCampaignsColumns from '../getCampaignsColumns';
import connect from '@app/utils/connect';
import { AUTOMATED_CAMPAIGNS } from '@app/constants/config';
import useCreateCampaign from '@app/components/hooks/campaigns/useCreateCampaign';
import useUpdateCampaign from '@app/components/hooks/campaigns/useUpdateCampaign';
import useArchiveCampaign from '@app/components/hooks/campaigns/useArchiveCampaign';
import useCampaigns from '@app/components/hooks/campaigns/useCampaigns';
import useCampaignsInsights from '@app/components/hooks/campaigns/useCampaignsInsights';
import MenuItemVerticalPopoverMenu from './MenuItemPopover';
import AutomatedParamDrawer from '@app/components/automated-param-modal/AutomatedParamDrawer';

type CampaignsInsightsTableProps = {
    client: Client;
    campaignsInsights: CampaignsInsights;
    isLoading: boolean;
    onDownloadCsv: () => void;
    query: any;
};

interface ColumnConfig {
    Header: string;
    accessor: string;
    Cell?: ({ value }: { value: any }) => React.ReactNode;
    headerClassName?: string;
    Footer?: () => React.ReactNode;
    width?: number;
    style?: React.CSSProperties;
    id?: string;
    format?: (value: any) => React.ReactNode;
    defaultSortDesc?: boolean;
}

const CampaignsInsightsTable = ({
    client,
    campaignsInsights,
    isLoading,
    onDownloadCsv,
    query,
}: CampaignsInsightsTableProps) => {
    const { t } = useTranslation(['ownedSocial', 'common']);
    const { data: campaigns } = useCampaigns(client.id);
    const antdTable = useRef(null);

    const showInitialMessage = campaigns?.length === 0;
    const [isCreateOpen, setCreateOpen] = React.useState(showInitialMessage);
    const [editId, setEditId] = React.useState(null);
    const openCreateModal = () => setCreateOpen(true);
    const closeCreateModal = () => setCreateOpen(false);
    const closeEditModal = () => setEditId(null);
    const isEditMode = !!editId;

    const campaign = useMemo(() => {
        if (!editId || !campaigns) return null;

        const foundCampaign = campaigns.find(c => c.id === editId);
        if (!foundCampaign) return null;

        // Ensure the campaign has the correct structure for the drawer
        // Use type assertion since the API response includes these fields but they're not in the type
        const campaignWithParams = foundCampaign as any;
        return {
            name: foundCampaign.name || '',
            description: campaignWithParams.description || '',
            automatedParameters: campaignWithParams.automatedParameters || [],
            // SP-8647: pass through the v2 nodes tree so the drawer can seed from it.
            automatedParametersDocument:
                campaignWithParams.automatedParametersDocument || null,
        };
    }, [editId, campaigns]);

    const showCreateButton = client.permissions.includes(AUTOMATED_CAMPAIGNS);
    const { mutate: create } = useCreateCampaign({ clientId: client.id });
    const { mutate: update } = useUpdateCampaign({
        clientId: client.id,
        id: editId,
    });
    const { mutate: archive } = useArchiveCampaign(client.id);
    const { refetch } = useCampaignsInsights(query, client.id);

    useEffect(() => {
        async function fetchCollections() {
            await refetch();
        }
        fetchCollections();
    }, [query, campaigns]);

    const antdTableProps = useMemo(() => {
        const columnsRaw = getCampaignsColumns(
            campaignsInsights?.total,
            {
                editCampaign: setEditId,
                deleteCampaign: archive,
                currency: client.currency?.code,
            },
            t,
        ) as ColumnConfig[];

        const columns: ColumnsType<any> = columnsRaw.map(c => {
            const {
                Header,
                accessor,
                format: formatter,
                id,
                defaultSortDesc,
            } = c;

            return {
                title: Header,
                dataIndex: accessor,
                key: accessor,
                ellipsis: true,
                width: id !== 'actions' ? 150 : 40,
                fixed: id === 'actions' ? 'right' : false,
                defaultSortOrder: defaultSortDesc ? 'descend' : undefined,
                sorter:
                    id !== 'actions'
                        ? (a, b) => {
                              const aValue = a[accessor];
                              const bValue = b[accessor];

                              if (
                                  aValue === undefined ||
                                  bValue === undefined
                              ) {
                                  return 0;
                              }
                              // Handle date sorting
                              if (accessor === 'addedAt') {
                                  const aDate = new Date(aValue).getTime();
                                  const bDate = new Date(bValue).getTime();
                                  if (isNaN(aDate) || isNaN(bDate)) return 0;
                                  return aDate - bDate;
                              }
                              if (typeof aValue === 'string') {
                                  return aValue.localeCompare(bValue);
                              }
                              return aValue - bValue;
                          }
                        : undefined,
                sortDirections: ['descend', 'ascend'] as SortOrder[],
                render: (value, record) => {
                    if (id === 'actions') {
                        return (
                            <MenuItemVerticalPopoverMenu
                                id={record.campaignId}
                                editCampaign={setEditId}
                                deleteCampaign={async () =>
                                    await archive(record.campaignId)
                                }
                            />
                        );
                    }
                    if (typeof formatter === 'function') {
                        return formatter(value);
                    }
                    return value;
                },
            };
        });

        // Merge addedAt from campaigns data (from /social-campaigns endpoint)
        // into campaignsInsights data (from /campaigns/insights endpoint)
        const enrichedDataSource = campaignsInsights?.values
            ? campaignsInsights.values.map(insight => {
                  // Find matching campaign by campaignId
                  const matchingCampaign = campaigns?.find(
                      c => c.id === insight.campaignId,
                  );
                  // Add addedAt from campaigns data if available
                  // Use type assertion since addedAt exists in API response but not in Campaign type
                  const campaignWithAddedAt = matchingCampaign as any;
                  return {
                      ...insight,
                      addedAt: campaignWithAddedAt?.addedAt || insight.addedAt,
                  };
              })
            : [];

        // Sort by addedAt descending by default (latest first)
        const sortedDataSource = enrichedDataSource.sort((a, b) => {
            const aDate = a.addedAt ? new Date(a.addedAt).getTime() : 0;
            const bDate = b.addedAt ? new Date(b.addedAt).getTime() : 0;
            // Sort descending (latest first)
            return bDate - aDate;
        });

        return {
            rowKey: 'id',
            columns,
            dataSource: sortedDataSource,
            loading: isLoading,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
            },
            scroll: { x: 'max-content' },
            summary: () => (
                <Table.Summary.Row className="summary-row">
                    {columns.map((column, index) => {
                        const accessor = (column as any).dataIndex;
                        const renderFn = (column as any).render;

                        if (index === 0) {
                            return (
                                <Table.Summary.Cell
                                    index={index}
                                    key={column.key}
                                    className="summary-cell-campaigns first-cell"
                                >
                                    {t('socialPostMetrics.total')}
                                </Table.Summary.Cell>
                            );
                        }

                        if (index === columns.length - 1) {
                            return (
                                <Table.Summary.Cell
                                    index={index}
                                    key={column.key}
                                    className="summary-cell-campaigns last-cell"
                                />
                            );
                        }

                        const fullData = campaignsInsights?.values || [];
                        const values = fullData.map(row => row[accessor]);
                        const filtered = values.filter(
                            v => v !== undefined && v !== null,
                        );

                        if (filtered.length === 0) {
                            return (
                                <Table.Summary.Cell
                                    index={index}
                                    key={column.key}
                                    className="summary-cell-campaigns"
                                />
                            );
                        }

                        const firstValue = filtered[0];
                        let summaryValue: any = null;

                        if (typeof firstValue === 'number') {
                            const total = filtered.reduce(
                                (sum, val) =>
                                    typeof val === 'number' ? sum + val : sum,
                                0,
                            );
                            summaryValue = total;
                        }

                        const renderedSummary =
                            summaryValue === null
                                ? null
                                : typeof renderFn === 'function'
                                  ? renderFn(summaryValue, {}, index)
                                  : summaryValue;

                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell-campaigns"
                            >
                                {renderedSummary}
                            </Table.Summary.Cell>
                        );
                    })}
                </Table.Summary.Row>
            ),
        };
    }, [campaignsInsights, campaigns, isLoading, archive, t]);

    return (
        <>
            <Flex
                align="center"
                justify="flex-end"
                style={{
                    margin: '12px 0 24px 0',
                }}
            >
                <Space>
                    <Button
                        onClick={onDownloadCsv}
                        data-test="download-csv-button"
                        disabled={isLoading}
                        icon={<DownloadOutlined />}
                        color="primary"
                        variant="outlined"
                    >
                        {t('socialPostMetrics.downloadCsv')}
                    </Button>
                    {showCreateButton && (
                        <Button
                            type="primary"
                            color="primary"
                            variant="solid"
                            onClick={openCreateModal}
                            data-test="open-creation-modal-button"
                            disabled={isLoading}
                            icon={<i className="fa-regular fa-pen" />}
                        >
                            {t('socialPostMetrics.createCampaign')}
                        </Button>
                    )}
                </Space>
            </Flex>

            <ErrorBoundary FallbackComponent={CrashedComponent}>
                <div data-test="campaigns-insights-list">
                    <style>
                        {`
                                .summary-row {
                                    height: 40px;
                                }
                                .summary-cell-campaigns {
                                    padding: 4px 8px !important;
                                }
                            `}
                    </style>
                    <Table
                        ref={antdTable}
                        {...antdTableProps}
                        bordered
                        rowClassName={(_, index) =>
                            index % 2 === 0
                                ? 'table-row-light'
                                : 'table-row-dark'
                        }
                        locale={{
                            emptyText: isLoading ? (
                                <Skeleton active paragraph={{ rows: 10 }} />
                            ) : (
                                <Empty />
                            ),
                        }}
                    />
                </div>
            </ErrorBoundary>

            <AutomatedParamDrawer
                onClose={closeCreateModal}
                title={t('socialPostMetrics.cancel')}
                isLoading={false}
                errorMsg={null}
                allValues={Object.values(campaignsInsights?.values || {})}
                type="campaigns"
                create={create}
                update={update}
                archive={archive}
                open={isCreateOpen}
            />

            <AutomatedParamDrawer
                onClose={closeEditModal}
                title={t('socialPostMetrics.editCampaign')}
                id={editId}
                isEditMode={isEditMode}
                value={campaign}
                isLoading={false}
                errorMsg={null}
                type="campaigns"
                create={create}
                update={update}
                archive={archive}
                open={!!editId}
            />
        </>
    );
};

export default connect(['client'])(CampaignsInsightsTable);
